import { useMemo } from 'react';
import type { PortfolioRow } from '../../portfolio';
import { combineInvestmentMix } from '../../investment';
import { MONTHS } from '../../when';
import InfoTip from '../InfoTip';

/**
 * THE PORTFOLIO HEADER PANEL (#435, Option B) — one banded header that OWNS the
 * roll-up's title, so the glance layer is no longer a floating summary card above
 * a second title (Vinnie, 22 Oct). The panel carries, in one bordered box:
 *  - top-left: the roll-up title + subtitle, with the cards/boards count folded in;
 *  - top-right (the hero): the three lifecycle counts — In delivery / In service /
 *    Go-Live overdue — the real meeting question of where the work stands;
 *  - a quiet bottom strip: the whole-portfolio Run-vs-Change split by card count.
 * The tabs + Filter/Display/Produce toolbar then attach to the table below (in
 * RollupTable), so there is one header, not two.
 *
 * Same `is-change` / `is-run` tones and bar shape as the board metrics strip, so the
 * board and the portfolio read as one family. It holds its FRAME and title while the
 * portfolio loads (the counts/bar fill in when the data arrives) so nothing shifts;
 * and it keeps the title even with nothing to classify, so the header is always there.
 */
export default function PortfolioMixBand({
  rows,
  loading,
  at,
}: {
  rows: PortfolioRow[];
  loading?: boolean;
  at?: number;
}) {
  const { mix, boards, projects, extra, inDelivery, inService, overdue } = useMemo(() => {
    const m = combineInvestmentMix(rows.map((r) => r.mix));
    // Boards that actually carry classified cards — what "across N boards" counts.
    const classified = rows.filter((r) => r.mix.total > 0);
    // Split so the total reconciles with the list below: the tracked projects are
    // the rows a reader sees in the roll-up; the rest are boards not yet set up as a
    // project, folded into this band but listed under "other boards", not as rows.
    const p = classified.filter((r) => r.tracked).length;
    // The lifecycle headline (#435 surface 2) — how many boards are being built vs
    // run, and how many have sailed past their go-live without being handed over.
    return {
      mix: m,
      boards: classified.length,
      projects: p,
      extra: classified.length - p,
      inDelivery: rows.filter((r) => r.tier === 'indelivery').length,
      inService: rows.filter((r) => r.tier === 'inservice').length,
      overdue: rows.filter((r) => r.overdue).length,
    };
  }, [rows]);

  const hasData = !!mix.label;
  const snap = new Date(at ?? Date.now());
  const snapLabel = `${snap.getDate()} ${MONTHS[snap.getMonth()]} ${snap.getFullYear()}`;

  return (
    <div className="pf-mixband pf-headpanel" data-reveal="portfolio-mix">
      <div className="pf-headpanel-top">
        <div className="pf-headpanel-title">
          <div className="pf-headpanel-h">Every project in the workspace</div>
          <div className="pf-sub">
            Departmental view · one row per project · snapshot of the week of {snapLabel}
            {hasData && (
              <>
                {' · '}
                {mix.total} {mix.total === 1 ? 'card' : 'cards'} across {boards}{' '}
                {boards === 1 ? 'board' : 'boards'}
                {projects > 0 && extra > 0 && (
                  <>
                    {' '}({projects} {projects === 1 ? 'project' : 'projects'} + {extra} not yet{' '}
                    {extra === 1 ? 'a project' : 'projects'})
                  </>
                )}
              </>
            )}
          </div>
        </div>
        {hasData && (
          <div className="pf-mixband-stats">
            <div className="pf-mixband-stat">
              <span className="pf-mixband-statn">{inDelivery}</span>
              <span className="pf-mixband-statl">In delivery</span>
            </div>
            <div className="pf-mixband-stat">
              <span className="pf-mixband-statn">{inService}</span>
              <span className="pf-mixband-statl">In service</span>
            </div>
            <div className={`pf-mixband-stat${overdue > 0 ? ' is-overdue' : ''}`}>
              <span className="pf-mixband-statn">{overdue}</span>
              <span className="pf-mixband-statl">Go-Live overdue</span>
            </div>
          </div>
        )}
      </div>

      {(hasData || loading) && (
        <div className="pf-headpanel-mix">
          <span className="pf-mixband-eyebrow">
            Run vs Change
            <InfoTip label="Run vs Change">
              How much of your work is <b>building something new (Change)</b> versus{' '}
              <b>keeping services running (Run)</b> — by card count across every board. Each card
              follows its type; to reclassify one, open it on its board → <b>Investment class</b> →
              Change or Run.
              <br />
              <br />
              Example: a “patch file servers” card counts as Run; a “new VPN gateway” counts as Change.
            </InfoTip>
          </span>
          <div className="pf-mixband-bar" aria-hidden="true">
            {hasData && mix.change > 0 && (
              <span className="pf-mixband-seg is-change" style={{ width: `${mix.changePct}%` }} />
            )}
            {hasData && mix.run > 0 && (
              <span className="pf-mixband-seg is-run" style={{ width: `${mix.runPct}%` }} />
            )}
          </div>
          <div className={`pf-mixband-legend${hasData ? '' : ' is-loading'}`}>
            <span className="pf-mixband-key">
              <span className="pf-mixband-swatch is-change" />
              {hasData ? (
                <>
                  <strong>{mix.changePct}%</strong> Change <span className="pf-mixband-gloss">· building</span>
                </>
              ) : (
                <>Change <span className="pf-mixband-gloss">· building</span></>
              )}
            </span>
            <span className="pf-mixband-key">
              <span className="pf-mixband-swatch is-run" />
              {hasData ? (
                <>
                  <strong>{mix.runPct}%</strong> Run <span className="pf-mixband-gloss">· keeping it running</span>
                </>
              ) : (
                <>Run <span className="pf-mixband-gloss">· keeping it running</span></>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
